import { useState } from 'react'

import type { FPlusExpressionValue } from '../../../types/templateModel'
import { UN_TEMPLATED_SUFFIX } from '../../fplusExpression/expressionType'
import type { ConstructorTypeInformation } from '../../fplusExpression/types'
import { functionDeclarationFromMetadata } from '../../templateFieldsTable/functionHelpers'
import { transformConstructorValuesToFPlusCode } from '../customExpressionToFPlus'
import { FPlusInlineEditContainer } from '../FPlusInlineEditContainer'
import { FPlusCustomCollectionEditor } from './FPlusCustomCollectionEditor'
import { FPlusSingleConstructorEditor } from './FPlusSingleConstructorEditor'
import type { CustomEditorProps } from './types'

export const FPlusConstructorExpressionEditor = ({
  onCancel,
  onChangeCode,
  initialHeightPx,
  typeInformation,
  expressionPath,
}: CustomEditorProps<ConstructorTypeInformation> & {
  expressionPath: string
}) => {
  const defaultValue = {
    name:
      typeInformation.isUnTemplated &&
      !typeInformation.name.endsWith(UN_TEMPLATED_SUFFIX)
        ? `${typeInformation.name}${UN_TEMPLATED_SUFFIX}`
        : typeInformation.name,
    arguments: [],
  }
  const [constructorCalls, setConstructorCalls] = useState(
    typeInformation.value?.length ? typeInformation.value : [defaultValue], // Default to an empty constructor call
  )

  const handleCodeChanged = (nextValue: FPlusExpressionValue) => {
    onChangeCode?.(
      typeInformation.functionMetadata
        ? {
            ...nextValue,
            fplusSource: `${functionDeclarationFromMetadata(typeInformation.functionMetadata)} =\n    ${nextValue.fplusSource}`,
          }
        : nextValue,
    )
  }

  return (
    <FPlusInlineEditContainer
      code={transformConstructorValuesToFPlusCode({
        value: constructorCalls,
        collectionType: typeInformation.collectionType,
      })}
      onCancel={onCancel}
      initialHeightPx={initialHeightPx}
      onChangeCode={handleCodeChanged}
      className="p-2 pb-10 cursor-auto"
    >
      {typeInformation.collectionType ? (
        <FPlusCustomCollectionEditor
          currentValue={constructorCalls}
          onChange={setConstructorCalls}
          emptyValue={{ name: typeInformation.name, arguments: [] }}
          renderEditor={(value, index) => (
            <FPlusSingleConstructorEditor
              typeInformation={typeInformation}
              currentValue={value}
              onChange={(nextValue) =>
                setConstructorCalls(
                  constructorCalls.toSpliced(index, 1, nextValue),
                )
              }
              expressionPath={expressionPath}
            />
          )}
        />
      ) : (
        <FPlusSingleConstructorEditor
          typeInformation={typeInformation}
          currentValue={constructorCalls.at(0) ?? defaultValue}
          onChange={(nextValue) => setConstructorCalls([nextValue])}
          expressionPath={expressionPath}
        />
      )}
    </FPlusInlineEditContainer>
  )
}
